import Button from "components/common/Button";
import DialogFormation from "components/common/DialogFormation";
import DraggableList from "components/common/DraggableList";
import Typography from "components/common/Typography";
import { uniq } from "lodash";
import React, { useEffect, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { reorder } from "utils/CommonFunctions";
import {
  fetchAssociatesMetadata,
  patchAssociatesMetadata,
  setDashboardWidgetPreference,
  setOpenReorderSection
} from "../dashboard/HotelUserDashboard.slice";
import MyPlansAndBrandDeadlines from "./MyPlansAndBrandDeadlines";
import PlanRecommendations from "./PlanRecommendations/index";
import PropertyAndTeamTracker from "./PropertyAndTeamTracker";
import Statistics from "./Statistics";

const componentMap: Record<string, React.FC> = {
  Statistics,
  MyPlansAndBrandDeadlines,
  PropertyAndTeamTracker,
  PlanRecommendations
};

interface Item {
  id: string;
  name: string;
}

interface ComponentMappingType {
  [key: string]: Item;
}

const ComponentMapping: ComponentMappingType = {
  Statistics: { id: "Statistics", name: "BOOST Summary Metrics" },
  MyPlansAndBrandDeadlines: {
    id: "MyPlansAndBrandDeadlines",
    name: "My Plans Overview & Brand Deadlines"
  },
  PropertyAndTeamTracker: { id: "PropertyAndTeamTracker", name: "Team Progress & Assigned Plans" },
  PlanRecommendations: { id: "PlanRecommendations", name: "Plan Suggestions" }
};

const defaultWidgetPreference = {
  dashboardWidgetPreference: ["Statistics", "MyPlansAndBrandDeadlines", "PropertyAndTeamTracker", "PlanRecommendations"]
};

const MyDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const dashboardWidgetPreference = useAppSelector(
    (state) => state?.hotelUserDashboardSlice?.dashboardWidgetPreference
  );
  const { isAbovePropertyUser } = useAppSelector((state) => state.app.loggedInUserDetails);

  const isReorderDialogOpen = useAppSelector((state) => state?.hotelUserDashboardSlice?.isReorderDialogOpen);

  const [newItems, setNewItems] = useState<[] | Item[]>([]);

  useEffect(() => {
    if (!isAbovePropertyUser) {
      (async () => {
        const res: any = await dispatch(fetchAssociatesMetadata({}));
        if (res?.payload?.dashboardWidgetPreference?.length) {
          dispatch(setDashboardWidgetPreference(res.payload));
        } else {
          dispatch(setDashboardWidgetPreference(defaultWidgetPreference));
        }
      })();
    } else {
      dispatch(setDashboardWidgetPreference(defaultWidgetPreference));
    }
  }, []);

  useEffect(() => {
    if (dashboardWidgetPreference && dashboardWidgetPreference?.dashboardWidgetPreference?.length) {
      const newOrder = dashboardWidgetPreference?.dashboardWidgetPreference?.map((data: any) => {
        return ComponentMapping[data];
      });
      setNewItems(newOrder);
    }
  }, [dashboardWidgetPreference]);

  const onDragEnd = ({ destination, source }: DropResult) => {
    // dropped outside the list
    if (!destination) return;
    const newItemss: any = reorder(newItems, source.index, destination.index);
    setNewItems(newItemss);
  };

  return (
    <div className="my-dashboard">
      {isReorderDialogOpen && (
        <DialogFormation
          title="Customize your Dashboard"
          openDialog={isReorderDialogOpen}
          handleClose={() => dispatch(setOpenReorderSection(false))}
          actionConfig={
            <div className="modal-config-wrapper">
              <Button variant="secondary" label="Cancel" onClick={() => dispatch(setOpenReorderSection(false))} />
              <Button
                variant="primary"
                label="Save"
                onClick={async () => {
                  const dataToBeSaved = newItems?.map((data) => data.id);
                  const uniqueAr = uniq(dataToBeSaved);
                  const data = {
                    dashboardWidgetPreference: uniqueAr,
                    lastViewedReleaseVersion: dashboardWidgetPreference?.lastViewedReleaseVersion
                  };
                  const dataPayload = {
                    payload: data,
                    update_type: "dashboardwidgetpreference"
                  };
                  const res = await dispatch(patchAssociatesMetadata(dataPayload));
                  dispatch(setDashboardWidgetPreference(res.payload));
                  dispatch(setOpenReorderSection(false));
                }}
              />
            </div>
          }
        >
          <div style={{ marginBottom: "24px" }}>
            <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
              Drag and drop to reorder
            </Typography>
          </div>
          {newItems?.length && <DraggableList items={newItems} onDragEnd={onDragEnd} />}
        </DialogFormation>
      )}
      {dashboardWidgetPreference?.dashboardWidgetPreference?.map((data: any) => {
        const Component = componentMap[data];
        return <Component key={data} />;
      })}
    </div>
  );
};

export default MyDashboard;
